/**
 * TableContextAdapterOrganism
 *
 * Bridges context execution data to a flat table rendering.
 * Flattens hierarchical sphere data into rows with configurable columns.
 */

import React, { useMemo, useState } from 'react';
import { useTranslation } from 'localization';
import { useContextData } from '../../../hooks/useContextData';
import { useContextView } from '../../../hooks/useContextView';
import type { ApiClients } from '../../../context/ApiClientsContext';

export interface TableContextAdapterOrganismProps {
  /** API clients from context */
  apiClients: ApiClients | null;
  /** Context view system name to load */
  viewSystemName: string;
  /** Root sphere ID to execute context from */
  rootSphereId: string;
  /** Results per page per level */
  limit?: number;
  /** Max depth for repeat levels */
  depth?: number;
  /** Callback when a row is selected */
  onRowSelect?: (sphereId: string) => void;
  /** Custom render for loading state */
  renderLoading?: () => React.ReactNode;
  /** Custom render for error state */
  renderError?: (error: string) => React.ReactNode;
  /** CSS class name */
  className?: string;
}

interface TableColumn {
  property: string;
  header: string;
  width?: number | 'auto';
  sortable?: boolean;
  renderer?: string;
}

/** Resolve column width to a CSS value, handling 'auto' and numeric widths */
function resolveColumnWidth(width: number | 'auto' | undefined): string | undefined {
  if (width === 'auto') return undefined;
  return width ? `${width}px` : undefined;
}

const TableContextAdapterOrganism: React.FC<TableContextAdapterOrganismProps> = ({
  apiClients,
  viewSystemName,
  rootSphereId,
  limit,
  depth,
  onRowSelect,
  renderLoading,
  renderError,
  className,
}) => {
  const { t } = useTranslation();
  const { view, loading: viewLoading, error: viewError } = useContextView(apiClients, viewSystemName);

  const contextSystemName = view?.context_ref ?? '';

  const {
    data,
    loading: dataLoading,
    error: dataError,
    cacheMetadata,
    refresh,
    fetchMore,
    hasMore,
  } = useContextData(apiClients, {
    systemName: contextSystemName,
    rootSphereId,
    limit,
    depth,
  });

  const loading = viewLoading || dataLoading;
  const error = viewError || dataError;

  const [sortColumn, setSortColumn] = useState<string | null>(null);
  const [sortAsc, setSortAsc] = useState(true);

  const tableConfig = useMemo(() => {
    if (!view) return null;
    const vc = view.view_config;
    return {
      columns: (vc.columns as TableColumn[]) ?? [],
      groupBy: vc.groupBy as string | undefined,
      groupCollapsed: vc.groupCollapsed as boolean | undefined,
      rowHeight: (vc.rowHeight as string) ?? 'standard',
      allowColumnReorder: vc.allowColumnReorder as boolean | undefined,
      allowRowSelection: vc.allowRowSelection as boolean | undefined,
      pageSize: vc.pageSize as number | undefined,
    };
  }, [view]);

  const rows = useMemo(() => {
    if (!data) return [];
    const allSpheres = Object.values(data.spheresByColumn).flat();
    if (sortColumn) {
      allSpheres.sort((a, b) => {
        const aRaw = a[sortColumn];
        const aVal = String(typeof aRaw === 'object' ? '' : (aRaw ?? ''));
        const bRaw = b[sortColumn];
        const bVal = String(typeof bRaw === 'object' ? '' : (bRaw ?? ''));
        return sortAsc ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
      });
    }
    return allSpheres;
  }, [data, sortColumn, sortAsc]);

  const handleSort = (property: string) => {
    if (sortColumn === property) {
      setSortAsc(!sortAsc);
    } else {
      setSortColumn(property);
      setSortAsc(true);
    }
  };

  if (loading && !data) {
    if (renderLoading) return <>{renderLoading()}</>;
    return <div className={className}>{t('context.common.loadingTable')}</div>;
  }

  if (error && !data) {
    if (renderError) return <>{renderError(error)}</>;
    return <div className={className}>{t('context.common.error', { message: error })}</div>;
  }

  if (!data || !view || !tableConfig) {
    return null;
  }

  const columns = tableConfig.columns.length > 0
    ? tableConfig.columns
    : [{ property: 'label', header: t('context.viewEditor.name'), sortable: true }];

  return (
    <div className={className} data-testid="table-context-adapter">
      <div className="table-context-adapter__header">
        <span className="table-context-adapter__title">
          {view.name?.default ?? view.system_name}
        </span>
        <button
          className="table-context-adapter__refresh"
          onClick={refresh}
          title={t('context.common.refreshData')}
          type="button"
        >
          ↻
        </button>
      </div>
      <table className={`table-context-adapter__table table-context-adapter__table--${tableConfig.rowHeight}`}>
        <thead>
          <tr>
            {columns.map((col) => (
              <th
                key={col.property}
                className="table-context-adapter__th"
                style={{ width: resolveColumnWidth(col.width) }}
                onClick={col.sortable ? () => handleSort(col.property) : undefined}
              >
                {typeof col.header === 'object' ? (col.header as Record<string, string>).default ?? col.property : col.header ?? col.property}
                {sortColumn === col.property && (
                  <span className="table-context-adapter__sort-indicator">
                    {sortAsc ? ' ▲' : ' ▼'}
                  </span>
                )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((sphere) => (
            <tr
              key={sphere.id}
              className="table-context-adapter__row"
              onClick={() => onRowSelect?.(sphere.id)}
            >
              {columns.map((col) => (
                <td key={col.property} className="table-context-adapter__td">
                  {String(sphere[col.property] ?? '')}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      {hasMore && (
        <button
          className="table-context-adapter__load-more"
          onClick={fetchMore}
          type="button"
        >
          {t('context.common.loadMore')}
        </button>
      )}
      {cacheMetadata && (
        <div className="table-context-adapter__cache-info" data-testid="cache-info">
          {cacheMetadata.structureHit ? t('context.common.cached') : t('context.common.fresh')} · {rows.length} {t('context.common.rows')}
        </div>
      )}
    </div>
  );
};

export default TableContextAdapterOrganism;
